import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';

import { Button } from '../button/Button';
import type { DropdownOption } from '../dropdown/Dropdown';
import { Dropdown } from '../dropdown/Dropdown';
import { IconChevronDownSmall, IconChevronTopSmall, IconCrossSmall } from '../icons';

export type FilterOption<T = string> = DropdownOption<T>;

type BaseFilterProps<T> = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly clearLabel?: string;
    readonly disabled?: boolean;
    readonly emptyMessage?: string;
    readonly formatMultiSelectCount?: (count: number) => ReactNode;
    readonly multiSelectCountLabel?: string;
    readonly onOpenChange?: (open: boolean) => void;
    readonly open?: boolean;
    readonly options: readonly FilterOption<T>[];
};

type SingleSelectFilterProps<T = string> = BaseFilterProps<T> & {
    readonly isMultiSelect?: false;
    readonly onValueChange?: (value: T | undefined) => void;
    readonly value?: T;
};

type MultiSelectFilterProps<T = string> = BaseFilterProps<T> & {
    readonly isMultiSelect: true;
    readonly onValueChange?: (values: readonly T[]) => void;
    readonly value?: readonly T[];
};

type FilterProps<T = string> = MultiSelectFilterProps<T> | SingleSelectFilterProps<T>;

const isSingleSelect = <T extends string>(props: FilterProps<T>): props is SingleSelectFilterProps<T> => {
    return props.isMultiSelect !== true;
};

const checkHasValue = <T extends string>(value: T | readonly T[] | undefined): boolean => {
    return !is.undefined(value) && (is.array(value) ? is.nonEmptyArray(value) : true);
};

const FILTER_ICON_SIZE = 12;

const getFilterIcon = (isOpen: boolean): JSX.Element => {
    if (isOpen) {
        return <IconChevronTopSmall size={FILTER_ICON_SIZE} />;
    }

    return <IconChevronDownSmall size={FILTER_ICON_SIZE} />;
};

const FilterClearButton = ({
    clearLabel,
    onClear,
}: {
    readonly clearLabel: string;
    readonly onClear: () => void;
}): JSX.Element => (
    <button
        aria-label={clearLabel}
        className="absolute right-[calc(var(--spacing-m)+var(--spacing-xs)+12px)] top-1/2 inline-flex size-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-accent hover:bg-indicator-highlighted-surface hover:text-accent-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring"
        title={clearLabel}
        type="button"
        onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClear();
        }}
        onKeyDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
    >
        <IconCrossSmall size={FILTER_ICON_SIZE} />
    </button>
);

const FilterTriggerContent = ({
    buttonText,
    isOpen,
    showClear,
}: {
    readonly buttonText: ReactNode;
    readonly isOpen: boolean;
    readonly showClear: boolean;
}): JSX.Element => (
    <>
        <span className="min-w-0 max-w-[12rem] truncate">{buttonText}</span>
        {showClear && <span aria-hidden={true} className="inline-flex size-4 shrink-0" />}
        <span className="inline-flex size-3 shrink-0 items-center justify-center">{getFilterIcon(isOpen)}</span>
    </>
);

const getButtonText = <T extends string>(
    children: ReactNode,
    value: T | readonly T[] | undefined,
    options: readonly FilterOption<T>[],
    isMultiSelect: boolean,
    multiSelectCountLabel: string | undefined,
    formatMultiSelectCount: ((count: number) => ReactNode) | undefined,
): ReactNode => {
    if (is.undefined(value) || (is.array(value) && value.length === 0)) {
        return children;
    }

    if (isMultiSelect && is.array(value)) {
        if (value.length === 1) {
            const option = options.find((opt) => opt.value === value[0]);

            return !is.undefined(option) ? option.label : children;
        }

        if (!is.undefined(formatMultiSelectCount)) {
            return formatMultiSelectCount(value.length);
        }

        return !is.undefined(multiSelectCountLabel) ? `${value.length} ${multiSelectCountLabel}` : String(value.length);
    }

    if (!is.array(value)) {
        const option = options.find((opt) => opt.value === value);

        return !is.undefined(option) ? option.label : children;
    }

    return children;
};

export const Filter = <T extends string>(props: FilterProps<T>): JSX.Element => {
    const {
        children,
        clearLabel,
        className,
        disabled = false,
        emptyMessage = 'No results found.',
        formatMultiSelectCount,
        multiSelectCountLabel,
        onOpenChange,
        open: controlledOpen,
        options,
        value,
    } = props;

    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const setOpen = onOpenChange ?? setInternalOpen;

    const hasValue = checkHasValue(value);
    const buttonText = getButtonText(
        children,
        value,
        options,
        !isSingleSelect(props),
        multiSelectCountLabel,
        formatMultiSelectCount,
    );

    const handleClear = (): void => {
        if (isSingleSelect(props)) {
            props.onValueChange?.(undefined);
        } else {
            props.onValueChange?.([]);
        }
    };

    const canClear = !is.undefined(props.onValueChange);
    const showClear = hasValue && !disabled && canClear && !is.undefined(clearLabel);
    const triggerButton = (
        <span className="relative inline-flex min-w-0 max-w-[14rem]">
            <Button
                className={clsx('min-w-0 max-w-[14rem]', className)}
                disabled={disabled}
                iconPosition="trailing"
                isActive={hasValue}
                size="sm"
                variant="secondary"
            >
                <FilterTriggerContent buttonText={buttonText} isOpen={open} showClear={showClear} />
            </Button>
            {showClear && !is.undefined(clearLabel) && (
                <FilterClearButton clearLabel={clearLabel} onClear={handleClear} />
            )}
        </span>
    );

    if (isSingleSelect(props)) {
        return (
            <Dropdown
                disabled={disabled}
                emptyMessage={emptyMessage}
                isMultiSelect={false}
                open={open}
                options={options}
                value={props.value}
                onOpenChange={setOpen}
                onValueChange={props.onValueChange}
            >
                {triggerButton}
            </Dropdown>
        );
    }

    return (
        <Dropdown
            disabled={disabled}
            emptyMessage={emptyMessage}
            isMultiSelect={true}
            open={open}
            options={options}
            value={props.value}
            onOpenChange={setOpen}
            onValueChange={props.onValueChange}
        >
            {triggerButton}
        </Dropdown>
    );
};
